import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ControlaiService } from '../../services/controlai.service';
import { ControlhelpService } from '../../services/controlhelp.service';
import { ControlflowService } from '../../services/controlflow.service';
import { OrgStructureService } from '../../services/org-structure.service';
import { UserSessionService } from '../../services/user-session.service';

const ASSISTANT_WORKFLOW_SOURCE = 'CONTROLAI_ASSISTANT';
const WORKFLOW_TYPE_PROCESS = 'PROCESS';
const WORKFLOW_TYPE_CHATBOT = 'CHATBOT';
const ASSISTANT_STAGE_TYPE_INTERACTION = 'INTERACTION';
const ASSISTANT_STAGE_TYPE_EXECUTE_API = 'EXECUTE_API';
const ASSISTANT_STAGE_TYPE_TIMER = 'TIMER';

interface WorkflowStep {
  id: number;
  type: string;
  status?: 'RASCUNHO' | 'ATIVO';
  savedId?: number | null;
  data?: any;
}

interface AssistantFlowButton {
  label: string;
  action: string;
  next_state: string;
  output: string;
}

interface AssistantFlowStage {
  id: string;
  title: string;
  text: string;
  output: string;
  stage_type: 'INTERACTION' | 'EXECUTE_API' | 'TIMER';
  default_next_state: string;
  timeout_seconds: number;
  timeout_next_state: string;
  response_execute_api: string;
  failure_output: string;
  cancel_output: string;
  allow_cancel_processing: boolean;
  buttons: AssistantFlowButton[];
}

@Component({
  selector: 'app-controlflow-editor',
  templateUrl: './controlflow-editor.component.html',
  styleUrls: ['./controlflow-editor.component.scss']
})
export class ControlflowEditorComponent implements OnInit {
  @ViewChild('emailBody') emailBody?: ElementRef<HTMLDivElement>;

  palette = [
    { type: 'inicio', title: 'Início', description: 'Disparo do fluxo' },
    { type: 'tarefa', title: 'Criar tarefa', description: 'Gera tarefas no ControlTask' },
    { type: 'chamado', title: 'Abrir chamado', description: 'Cria chamado no ControlHelp' },
    { type: 'email', title: 'Enviar e-mail', description: 'Dispara comunicação automática' },
    { type: 'condicao', title: 'Condição', description: 'Decisão com caminhos' },
    { type: 'fim', title: 'Conclusão', description: 'Final do fluxo' }
  ];

  workflowName = '';
  tenantId: number | null = null;
  userId: number | null = null;
  departments: any[] = [];
  categories: any[] = [];
  categoryOptions: string[] = [];
  subcategories: string[] = [];
  savingTask = false;
  savingTicket = false;
  savingEmail = false;
  savingWorkflow = false;
  taskSaved = false;
  ticketSaved = false;
  emailSaved = false;
  workflowSaved = false;
  workflowId: number | null = null;
  workflowSourceModule: string | null = null;
  workflowSourceRefId: number | null = null;
  workflowType: 'PROCESS' | 'CHATBOT' = WORKFLOW_TYPE_PROCESS;
  assistantCommandId: number | null = null;
  assistantCommands: Array<{ id: number; title: string; version: number }> = [];
  loadingAssistantCommands = false;
  assistantStages: AssistantFlowStage[] = [];
  selectedAssistantStageIndex = 0;
  draggingAssistantStageIndex: number | null = null;
  assistantStageDragOverIndex: number | null = null;
  taskForm = {
    title: '',
    description: '',
    visibility: 'PRIVATE',
    priority: 'MEDIUM',
    start_date: '',
    due_date: ''
  };
  ticketForm = {
    title: '',
    description: '',
    priority: 'MEDIUM',
    department_id: '',
    category: '',
    subcategory: ''
  };
  emailForm = {
    to: '',
    subject: '',
    body: ''
  };
  rows: WorkflowStep[][] = [[{ id: 1, type: 'inicio', status: 'RASCUNHO' }]];
  selectedStep: WorkflowStep | null = this.rows[0][0];
  private nextId = 2;
  readonly noActionState = 'NO_ACTION';

  constructor(
    private controlaiService: ControlaiService,
    private controlflowService: ControlflowService,
    private controlhelpService: ControlhelpService,
    private orgStructureService: OrgStructureService,
    private session: UserSessionService,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    const user = this.session.getUser();
    this.tenantId = user?.tenant_id ?? null;
    this.userId = user?.id ?? null;

    if (this.tenantId) {
      this.loadDepartments();
      this.loadCategories();
      this.loadAssistantCommands();
    }

    const paramId = this.route.snapshot.paramMap.get('id');
    if (paramId && paramId !== 'novo') {
      const workflowId = Number(paramId);
      if (!Number.isNaN(workflowId)) {
        this.loadWorkflow(workflowId);
      }
    }
  }

  get selectedAssistantStage(): AssistantFlowStage | null {
    return this.assistantStages[this.selectedAssistantStageIndex] || null;
  }

  get isAssistantWorkflow() {
    return this.workflowType === WORKFLOW_TYPE_CHATBOT;
  }

  addRight(rowIndex: number) {
    const step = this.createStep('tarefa');
    this.rows[rowIndex].push(step);
    this.selectedStep = step;
  }

  addDown(rowIndex: number) {
    const step = this.createStep('tarefa');
    this.rows.splice(rowIndex + 1, 0, [step]);
    this.selectedStep = step;
  }

  removeStep(rowIndex: number, colIndex: number) {
    const [removed] = this.rows[rowIndex].splice(colIndex, 1);
    if (this.selectedStep?.id === removed?.id) {
      this.selectedStep = this.rows[rowIndex]?.[0] || this.rows[0]?.[0] || null;
    }
    if (!this.rows[rowIndex].length) {
      this.rows.splice(rowIndex, 1);
    }
  }

  selectStep(step: WorkflowStep) {
    this.selectedStep = step;
    this.syncFormFromStep(step);
    this.resetSaveFlags();
  }

  clearSelection() {
    this.selectedStep = null;
  }

  selectAssistantStage(index: number) {
    if (index < 0 || index >= this.assistantStages.length) {
      return;
    }
    this.selectedAssistantStageIndex = index;
    this.resetSaveFlags();
  }

  onAssistantStageDragStart(index: number, event: DragEvent) {
    if (index < 0 || index >= this.assistantStages.length) {
      return;
    }
    this.draggingAssistantStageIndex = index;
    this.assistantStageDragOverIndex = index;
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', String(index));
    }
  }

  onAssistantStageDragOver(index: number, event: DragEvent) {
    if (this.draggingAssistantStageIndex === null) {
      return;
    }
    if (index < 0 || index >= this.assistantStages.length) {
      return;
    }
    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move';
    }
    this.assistantStageDragOverIndex = index;
  }

  onAssistantStageDrop(index: number, event: DragEvent) {
    event.preventDefault();
    const fallbackSource = Number(event.dataTransfer?.getData('text/plain'));
    const sourceIndex =
      this.draggingAssistantStageIndex !== null
        ? this.draggingAssistantStageIndex
        : Number.isFinite(fallbackSource)
        ? fallbackSource
        : null;
    const targetIndex = Number(index);

    if (
      sourceIndex === null ||
      !Number.isInteger(sourceIndex) ||
      !Number.isInteger(targetIndex) ||
      sourceIndex < 0 ||
      targetIndex < 0 ||
      sourceIndex >= this.assistantStages.length ||
      targetIndex >= this.assistantStages.length
    ) {
      this.onAssistantStageDragEnd();
      return;
    }

    if (sourceIndex === targetIndex) {
      this.onAssistantStageDragEnd();
      return;
    }

    const selectedStage = this.selectedAssistantStage;
    const [movedStage] = this.assistantStages.splice(sourceIndex, 1);
    if (!movedStage) {
      this.onAssistantStageDragEnd();
      return;
    }

    const finalIndex = sourceIndex < targetIndex ? targetIndex - 1 : targetIndex;
    this.assistantStages.splice(finalIndex, 0, movedStage);
    this.ensureAssistantStageDefaultTransitions();

    if (selectedStage) {
      const nextSelectedIndex = this.assistantStages.indexOf(selectedStage);
      this.selectedAssistantStageIndex = nextSelectedIndex >= 0 ? nextSelectedIndex : 0;
    } else {
      this.selectedAssistantStageIndex = Math.max(0, Math.min(finalIndex, this.assistantStages.length - 1));
    }

    this.resetSaveFlags();
    this.onAssistantStageDragEnd();
  }

  onAssistantStageDragEnd() {
    this.draggingAssistantStageIndex = null;
    this.assistantStageDragOverIndex = null;
  }

  addAssistantStage() {
    const newStage = this.buildDefaultAssistantStage(this.assistantStages.length + 1);
    this.assistantStages.push(newStage);
    this.ensureAssistantStageDefaultTransitions();
    this.selectedAssistantStageIndex = this.assistantStages.length - 1;
    this.resetSaveFlags();
  }

  removeAssistantStage(index: number) {
    if (this.assistantStages.length <= 1) {
      return;
    }
    this.assistantStages.splice(index, 1);
    this.ensureAssistantStageDefaultTransitions();
    if (this.selectedAssistantStageIndex >= this.assistantStages.length) {
      this.selectedAssistantStageIndex = this.assistantStages.length - 1;
    }
    this.resetSaveFlags();
  }

  duplicateAssistantStage(index: number) {
    const source = this.assistantStages[index];
    if (!source) {
      return;
    }
    const duplicatedId = this.buildDuplicatedAssistantStageId(source.id);
    const duplicatedStage: AssistantFlowStage = {
      id: duplicatedId,
      title: source.title ? `${source.title} (cópia)` : `Etapa ${index + 2}`,
      text: source.text || '',
      output: source.output || '',
      stage_type: source.stage_type || ASSISTANT_STAGE_TYPE_INTERACTION,
      default_next_state: source.default_next_state || '',
      timeout_seconds: Number(source.timeout_seconds || 90),
      timeout_next_state: source.timeout_next_state || '',
      response_execute_api: source.response_execute_api || '',
      failure_output: source.failure_output || '',
      cancel_output: source.cancel_output || '',
      allow_cancel_processing: Boolean(source.allow_cancel_processing),
      buttons: (source.buttons || []).map((button) => ({
        label: button.label || '',
        action: button.action || 'CUSTOM_ACTION',
        next_state: button.next_state || '',
        output: button.output || ''
      }))
    };
    this.assistantStages.splice(index + 1, 0, duplicatedStage);
    this.ensureAssistantStageDefaultTransitions();
    this.selectedAssistantStageIndex = index + 1;
    this.resetSaveFlags();
  }

  addAssistantButton(stage: AssistantFlowStage | null = this.selectedAssistantStage) {
    if (!stage) {
      return;
    }
    stage.buttons.push(this.createEmptyAssistantButton());
    this.resetSaveFlags();
  }

  removeAssistantButton(stage: AssistantFlowStage | null, buttonIndex: number) {
    if (!stage) {
      return;
    }
    stage.buttons.splice(buttonIndex, 1);
    this.resetSaveFlags();
  }

  onAssistantStageIdBlur(stage: AssistantFlowStage | null) {
    if (!stage) {
      return;
    }
    stage.id = this.normalizeAssistantState(stage.id) || stage.id || `STAGE_${this.selectedAssistantStageIndex + 1}`;
  }

  onAssistantButtonActionBlur(button: AssistantFlowButton) {
    button.action = this.normalizeAssistantState(button.action) || 'CUSTOM_ACTION';
  }

  onAssistantButtonNextStateBlur(button: AssistantFlowButton) {
    button.next_state = this.normalizeAssistantState(button.next_state || '') || '';
  }

  onAssistantDefaultNextStateBlur(stage: AssistantFlowStage | null) {
    if (!stage) {
      return;
    }
    const raw = String(stage.default_next_state || '').trim().toUpperCase();
    if (!raw) {
      stage.default_next_state = this.noActionState;
      return;
    }
    if (raw === this.noActionState) {
      stage.default_next_state = this.noActionState;
      return;
    }
    stage.default_next_state = this.normalizeAssistantState(raw) || this.noActionState;
  }

  onAssistantTimeoutNextStateBlur(stage: AssistantFlowStage | null) {
    if (!stage) {
      return;
    }
    const raw = String(stage.timeout_next_state || '').trim().toUpperCase();
    if (!raw) {
      stage.timeout_next_state = this.noActionState;
      return;
    }
    if (raw === this.noActionState) {
      stage.timeout_next_state = this.noActionState;
      return;
    }
    stage.timeout_next_state = this.normalizeAssistantState(raw) || this.noActionState;
  }

  onAssistantStageTypeChange(stage: AssistantFlowStage | null) {
    if (!stage) {
      return;
    }
    const nextValue = String(stage.stage_type || '')
      .trim()
      .toUpperCase();
    if (nextValue === ASSISTANT_STAGE_TYPE_EXECUTE_API) {
      stage.stage_type = ASSISTANT_STAGE_TYPE_EXECUTE_API;
    } else if (nextValue === ASSISTANT_STAGE_TYPE_TIMER) {
      stage.stage_type = ASSISTANT_STAGE_TYPE_TIMER;
    } else {
      stage.stage_type = ASSISTANT_STAGE_TYPE_INTERACTION;
    }
    if (stage.stage_type === ASSISTANT_STAGE_TYPE_INTERACTION) {
      stage.timeout_seconds = 90;
      stage.timeout_next_state = this.noActionState;
      stage.allow_cancel_processing = false;
      stage.response_execute_api = '';
    } else {
      if (!stage.timeout_seconds || stage.timeout_seconds < 5) {
        stage.timeout_seconds = 90;
      }
      if (!String(stage.timeout_next_state || '').trim()) {
        stage.timeout_next_state = this.noActionState;
      }
      if (stage.stage_type === ASSISTANT_STAGE_TYPE_EXECUTE_API && !stage.allow_cancel_processing) {
        stage.allow_cancel_processing = true;
      }
    }
    this.resetSaveFlags();
  }

  onWorkflowTypeChange() {
    this.workflowType = this.workflowType === WORKFLOW_TYPE_CHATBOT ? WORKFLOW_TYPE_CHATBOT : WORKFLOW_TYPE_PROCESS;
    if (this.workflowType === WORKFLOW_TYPE_CHATBOT) {
      this.selectedStep = null;
      if (!this.assistantStages.length) {
        this.assistantStages = [this.buildDefaultAssistantStage(1)];
      }
      this.ensureAssistantStageDefaultTransitions();
      this.selectedAssistantStageIndex = Math.min(
        Math.max(this.selectedAssistantStageIndex, 0),
        this.assistantStages.length - 1
      );
    } else if (!this.rows.length) {
      this.rows = [[{ id: 1, type: 'inicio', status: 'RASCUNHO' }]];
      this.selectedStep = this.rows[0][0];
      this.nextId = this.computeNextId();
    }
    this.resetSaveFlags();
  }

  saveWorkflow() {
    if (!this.tenantId || !this.userId || !this.workflowName) {
      return;
    }
    if (this.isAssistantWorkflow && !this.assistantCommandId) {
      return;
    }
    if (this.savingWorkflow) {
      return;
    }
    const workflowType = this.isAssistantWorkflow ? WORKFLOW_TYPE_CHATBOT : WORKFLOW_TYPE_PROCESS;
    const sourceModule = this.isAssistantWorkflow ? ASSISTANT_WORKFLOW_SOURCE : null;
    const sourceRefId = this.isAssistantWorkflow ? this.assistantCommandId : null;
    this.savingWorkflow = true;
    const payload = {
      tenant_id: this.tenantId,
      name: this.workflowName,
      status: 'ATIVO',
      created_by: this.userId,
      updated_by: this.userId,
      workflow_type: workflowType,
      source_module: sourceModule,
      source_ref_id: sourceRefId,
      assistant_command_id: sourceRefId,
      steps: this.isAssistantWorkflow ? this.serializeAssistantStagesToSteps() : this.serializeSteps()
    };
    const request$ = this.workflowId
      ? this.controlflowService.updateWorkflow(this.workflowId, payload)
      : this.controlflowService.createWorkflow(payload);
    request$.subscribe({
      next: (response: any) => {
        this.workflowId = this.workflowId ?? response?.id ?? null;
        this.workflowSaved = true;
        this.savingWorkflow = false;
      },
      error: () => {
        this.savingWorkflow = false;
      }
    });
  }

  handleTypeChange(step: WorkflowStep) {
    step.status = 'RASCUNHO';
    step.savedId = null;
    step.data = null;
    this.selectStep(step);
    this.resetSaveFlags();
  }

  loadDepartments() {
    if (!this.tenantId) {
      return;
    }
    this.orgStructureService.getDepartments(this.tenantId).subscribe({
      next: (rows) => (this.departments = rows || []),
      error: () => (this.departments = [])
    });
  }

  loadCategories(departmentId?: number | null) {
    if (!this.tenantId) {
      return;
    }
    this.controlhelpService.getCategories(this.tenantId, departmentId ?? null).subscribe({
      next: (rows) => {
        this.categories = rows || [];
        this.categoryOptions = Array.from(
          new Set(this.categories.map((item: any) => item.name).filter(Boolean))
        );
        this.updateSubcategories();
      },
      error: () => {
        this.categories = [];
        this.categoryOptions = [];
        this.subcategories = [];
      }
    });
  }

  loadAssistantCommands() {
    if (!this.tenantId) {
      this.assistantCommands = [];
      return;
    }
    this.loadingAssistantCommands = true;
    this.controlaiService.listCommands(this.tenantId, '', 1).subscribe({
      next: (rows: any[]) => {
        this.assistantCommands = (rows || [])
          .map((item) => ({
            id: Number(item?.id || 0),
            title: String(item?.title || '').trim(),
            version: Number(item?.version || 1)
          }))
          .filter((item) => item.id > 0);
        if (
          this.assistantCommandId &&
          !this.assistantCommands.some((item) => item.id === this.assistantCommandId)
        ) {
          this.assistantCommandId = null;
        }
        this.loadingAssistantCommands = false;
      },
      error: () => {
        this.assistantCommands = [];
        this.loadingAssistantCommands = false;
      }
    });
  }

  updateSubcategories() {
    if (!this.ticketForm.category) {
      this.subcategories = [];
      return;
    }
    const items = this.categories.filter((item: any) => item.name === this.ticketForm.category);
    this.subcategories = Array.from(new Set(items.map((item: any) => item.subcategory).filter(Boolean)));
  }

  onDepartmentChange() {
    const departmentId = Number(this.ticketForm.department_id);
    this.ticketForm.category = '';
    this.ticketForm.subcategory = '';
    if (Number.isNaN(departmentId)) {
      this.loadCategories();
      return;
    }
    this.loadCategories(departmentId);
  }

  onCategoryChange() {
    this.ticketForm.subcategory = '';
    this.updateSubcategories();
  }

  saveTask() {
    if (!this.tenantId || !this.userId || !this.taskForm.title || !this.selectedStep) {
      return;
    }
    if (this.savingTask) {
      return;
    }
    this.savingTask = true;
    const wasSaved = !!this.selectedStep.savedId;
    const payload: any = {
      tenant_id: this.tenantId,
      title: this.taskForm.title,
      description: this.taskForm.description || null,
      visibility: this.taskForm.visibility,
      status: 'ATIVO',
      priority: this.taskForm.priority,
      start_date: this.taskForm.start_date || null,
      due_date: this.taskForm.due_date || null,
      created_by: this.userId
    };
    const request$ = this.selectedStep.savedId
      ? this.controlflowService.updateTask(this.selectedStep.savedId, payload)
      : this.controlflowService.createTask(payload);
    request$.subscribe({
      next: (response: any) => {
        if (!this.selectedStep) {
          this.savingTask = false;
          return;
        }
        this.selectedStep.savedId = this.selectedStep.savedId ?? response?.id ?? null;
        this.selectedStep.status = 'ATIVO';
        const createdAt = (this.selectedStep.data as any)?.['created_at'] || new Date().toISOString();
        this.selectedStep.data = { ...this.taskForm, created_at: createdAt };
        this.savingTask = false;
        this.taskSaved = true;
        if (!wasSaved) {
          this.addStepBelowCurrent('tarefa');
        }
      },
      error: () => {
        this.savingTask = false;
      }
    });
  }

  saveTicket() {
    if (!this.tenantId || !this.userId || !this.ticketForm.title || !this.ticketForm.description || !this.selectedStep) {
      return;
    }
    if (this.savingTicket) {
      return;
    }
    const departmentId = Number(this.ticketForm.department_id);
    const payload: any = {
      tenant_id: this.tenantId,
      title: this.ticketForm.title,
      description: this.ticketForm.description,
      priority: this.ticketForm.priority,
      department_id: Number.isNaN(departmentId) ? null : departmentId,
      category: this.ticketForm.category || null,
      subcategory: this.ticketForm.subcategory || null,
      created_by: this.userId,
      status: 'ATIVO'
    };
    this.savingTicket = true;
    const wasSaved = !!this.selectedStep.savedId;
    const request$ = this.selectedStep.savedId
      ? this.controlflowService.updateTicket(this.selectedStep.savedId, payload)
      : this.controlflowService.createTicket(payload);
    request$.subscribe({
      next: (response: any) => {
        if (!this.selectedStep) {
          this.savingTicket = false;
          return;
        }
        this.selectedStep.savedId = this.selectedStep.savedId ?? response?.id ?? null;
        this.selectedStep.status = 'ATIVO';
        const createdAt = (this.selectedStep.data as any)?.['created_at'] || new Date().toISOString();
        this.selectedStep.data = { ...this.ticketForm, created_at: createdAt };
        this.savingTicket = false;
        this.ticketSaved = true;
        if (!wasSaved) {
          this.addStepBelowCurrent('chamado');
        }
      },
      error: () => {
        this.savingTicket = false;
      }
    });
  }

  saveEmail() {
    if (!this.selectedStep || !this.tenantId || !this.userId) {
      return;
    }
    const payload: any = {
      tenant_id: this.tenantId,
      created_by: this.userId,
      recipients: this.emailForm.to,
      subject: this.emailForm.subject,
      body_html: this.emailForm.body,
      status: 'ATIVO'
    };
    this.savingEmail = true;
    const wasSaved = !!this.selectedStep.savedId;
    const request$ = this.selectedStep.savedId
      ? this.controlflowService.updateEmail(this.selectedStep.savedId, payload)
      : this.controlflowService.createEmail(payload);
    request$.subscribe({
      next: (response: any) => {
        if (!this.selectedStep) {
          this.savingEmail = false;
          return;
        }
        this.selectedStep.savedId = this.selectedStep.savedId ?? response?.id ?? null;
        this.selectedStep.status = 'ATIVO';
        const createdAt = (this.selectedStep.data as any)?.['created_at'] || new Date().toISOString();
        this.selectedStep.data = { ...this.emailForm, created_at: createdAt };
        this.savingEmail = false;
        this.emailSaved = true;
        if (!wasSaved) {
          this.addStepBelowCurrent('email');
        }
      },
      error: () => {
        this.savingEmail = false;
      }
    });
  }

  formatEmail(command: string) {
    this.emailBody?.nativeElement.focus();
    document.execCommand(command, false);
  }

  updateEmailBody(event: Event) {
    const target = event.target as HTMLDivElement;
    this.emailForm.body = target.innerHTML || '';
  }

  getStepTitle(step: WorkflowStep) {
    return (step.data as any)?.['title'] || '—';
  }

  getStepSubject(step: WorkflowStep) {
    return (step.data as any)?.['subject'] || '—';
  }

  getStepCreatedAt(step: WorkflowStep) {
    return (step.data as any)?.['created_at'] || null;
  }

  getAssistantStageSummary(stage: AssistantFlowStage, index: number) {
    return stage.title || stage.id || `Etapa ${index + 1}`;
  }

  getAssistantStageNumberLabel(index: number) {
    return `Etapa ${index + 1}`;
  }

  getAssistantStageTypeLabel(stage: AssistantFlowStage) {
    const type = this.normalizeAssistantStageType(stage?.stage_type, stage?.id);
    if (type === ASSISTANT_STAGE_TYPE_EXECUTE_API) {
      return 'Executar API';
    }
    if (type === ASSISTANT_STAGE_TYPE_TIMER) {
      return 'Timer';
    }
    return 'Interação';
  }

  getAssistantNextStateOptions(currentIndex: number) {
    const options = this.assistantStages
      .map((stage) => this.normalizeAssistantState(stage.id))
      .filter(Boolean) as string[];
    return options.filter((id, index) => id && index !== currentIndex);
  }

  private createStep(type: string): WorkflowStep {
    return { id: this.nextId++, type, status: 'RASCUNHO' };
  }

  private addStepBelowCurrent(type: string) {
    if (!this.selectedStep) {
      return;
    }
    const position = this.findStepPosition(this.selectedStep.id);
    if (!position) {
      return;
    }
    const step = this.createStep(type);
    this.rows.splice(position.row + 1, 0, [step]);
    this.selectedStep = step;
    this.syncFormFromStep(step);
  }

  private findStepPosition(stepId: number) {
    for (let rowIndex = 0; rowIndex < this.rows.length; rowIndex += 1) {
      const colIndex = this.rows[rowIndex].findIndex((step) => step.id === stepId);
      if (colIndex >= 0) {
        return { row: rowIndex, col: colIndex };
      }
    }
    return null;
  }

  private syncFormFromStep(step: WorkflowStep) {
    const data = step.data || {};
    if (step.type === 'tarefa') {
      this.taskForm = {
        title: (data as any)['title'] || '',
        description: (data as any)['description'] || '',
        visibility: (data as any)['visibility'] || 'PRIVATE',
        priority: (data as any)['priority'] || 'MEDIUM',
        start_date: (data as any)['start_date'] || '',
        due_date: (data as any)['due_date'] || ''
      };
    } else if (step.type === 'chamado') {
      this.ticketForm = {
        title: (data as any)['title'] || '',
        description: (data as any)['description'] || '',
        priority: (data as any)['priority'] || 'MEDIUM',
        department_id: (data as any)['department_id'] || '',
        category: (data as any)['category'] || '',
        subcategory: (data as any)['subcategory'] || ''
      };
      this.updateSubcategories();
    } else if (step.type === 'email') {
      this.emailForm = {
        to: (data as any)['to'] || '',
        subject: (data as any)['subject'] || '',
        body: (data as any)['body'] || ''
      };
      if (this.emailBody?.nativeElement) {
        this.emailBody.nativeElement.innerHTML = this.emailForm.body || '';
      }
    }
  }

  private resetSaveFlags() {
    this.taskSaved = false;
    this.ticketSaved = false;
    this.emailSaved = false;
    this.workflowSaved = false;
  }

  private serializeSteps() {
    return this.rows.map((row, rowIndex) =>
      row.map((step, colIndex) => ({
        id: step.id,
        type: step.type,
        status: step.status || 'RASCUNHO',
        saved_id: step.savedId ?? null,
        row: rowIndex,
        col: colIndex,
        data: step.data || null
      }))
    );
  }

  private serializeAssistantStagesToSteps() {
    return this.assistantStages.map((stage, index) => {
      const stageId = this.normalizeAssistantState(stage.id) || `STAGE_${index + 1}`;
      const stageType = this.normalizeAssistantStageType(stage.stage_type, stageId);
      const legacyStageAction = this.mapAssistantStageTypeToAction(stageType, stageId);
      const timeoutSeconds =
        stageType === ASSISTANT_STAGE_TYPE_INTERACTION
          ? null
          : Math.min(3600, Math.max(5, Number(stage.timeout_seconds || 90)));
      const timeoutNextState =
        stageType === ASSISTANT_STAGE_TYPE_INTERACTION
          ? this.noActionState
          : (() => {
              const raw = String(stage.timeout_next_state || '').trim().toUpperCase();
              if (!raw || raw === this.noActionState) {
                return this.noActionState;
              }
              return this.normalizeAssistantState(raw) || this.noActionState;
            })();
      return [
        {
          id: index + 1,
          type: 'condicao',
          status: 'ATIVO',
          saved_id: null,
          row: index,
          col: 0,
          data: {
            source: ASSISTANT_WORKFLOW_SOURCE,
            stage_id: stageId,
            title: stage.title || stageId,
            text: stage.text || null,
            output: stage.output || null,
            stage_type: stageType,
            stage_action: legacyStageAction,
            default_next_state:
              (() => {
                const raw = String(stage.default_next_state || '').trim().toUpperCase();
                if (!raw || raw === this.noActionState) {
                  return this.noActionState;
                }
                return this.normalizeAssistantState(raw) || this.noActionState;
              })(),
            timeout_seconds: timeoutSeconds,
            processing_timeout_seconds: timeoutSeconds,
            response_execute_api: stage.response_execute_api || null,
            success_output: stage.response_execute_api || null,
            timeout_next_state: timeoutNextState,
            failure_output: stage.failure_output || null,
            cancel_output: stage.cancel_output || null,
            allow_cancel_processing: stageType === ASSISTANT_STAGE_TYPE_EXECUTE_API ? (stage.allow_cancel_processing ? 1 : 0) : 0,
            buttons: (stage.buttons || []).map((button) => ({
              label: button.label || 'Botão',
              action: this.normalizeAssistantState(button.action) || 'CUSTOM_ACTION',
              next_state: this.normalizeAssistantState(button.next_state) || null,
              output: button.output || null
            }))
          }
        }
      ];
    });
  }

  private loadWorkflow(workflowId: number) {
    if (!this.tenantId) {
      return;
    }
    this.controlflowService.getWorkflow(this.tenantId, workflowId).subscribe({
      next: (workflow) => {
        this.workflowId = workflow?.id ?? workflowId;
        this.workflowName = workflow?.name ?? '';
        this.workflowSourceModule = String(workflow?.source_module || '').trim() || null;
        const sourceRefId = Number(workflow?.source_ref_id);
        this.workflowSourceRefId = Number.isFinite(sourceRefId) ? sourceRefId : null;
        const workflowTypeRaw = String(workflow?.workflow_type || '').trim().toUpperCase();
        this.assistantCommandId = Number(workflow?.assistant_command_id || workflow?.source_ref_id || 0) || null;

        const rows = Array.isArray(workflow?.steps_json) ? workflow.steps_json : this.parseStepsJson(workflow?.steps_json);
        const assistantModeDetected = this.detectAssistantWorkflowMode(rows, this.workflowSourceModule);
        this.workflowType =
          workflowTypeRaw === WORKFLOW_TYPE_CHATBOT || assistantModeDetected
            ? WORKFLOW_TYPE_CHATBOT
            : WORKFLOW_TYPE_PROCESS;

        if (this.isAssistantWorkflow) {
          this.assistantStages = this.parseAssistantStages(rows);
          if (!this.assistantStages.length) {
            this.assistantStages = [this.buildDefaultAssistantStage(1)];
          }
          this.ensureAssistantStageDefaultTransitions();
          this.selectedAssistantStageIndex = 0;
          this.selectedStep = null;
          return;
        }

        const normalizedRows = rows.map((row: any) => (Array.isArray(row) ? row : [row]));
        if (normalizedRows.length) {
          this.rows = normalizedRows.map((row: any[]) =>
            row.map((step: any) => ({
              id: Number(step.id),
              type: step.type,
              status: step.status || 'RASCUNHO',
              savedId: step.saved_id ?? null,
              data: step.data || null
            }))
          );
          this.selectedStep = this.rows[0][0] || null;
          if (this.selectedStep) {
            this.syncFormFromStep(this.selectedStep);
          }
          this.nextId = this.computeNextId();
        }
      },
      error: () => {}
    });
  }

  private parseStepsJson(raw: any) {
    if (!raw) {
      return [];
    }
    try {
      const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
      return Array.isArray(parsed) ? parsed : [];
    } catch (_) {
      return [];
    }
  }

  private detectAssistantWorkflowMode(rows: any[], sourceModule: string | null) {
    if (String(sourceModule || '').trim().toUpperCase() === ASSISTANT_WORKFLOW_SOURCE) {
      return true;
    }
    return rows.some((row) => {
      const steps = Array.isArray(row) ? row : [row];
      return steps.some((step) => {
        const source = String(step?.data?.source || '').trim().toUpperCase();
        return source === ASSISTANT_WORKFLOW_SOURCE;
      });
    });
  }

  private parseAssistantStages(rows: any[]) {
    const flat: Array<{ step: any; row: number; col: number }> = [];
    rows.forEach((group, rowIndex) => {
      const steps = Array.isArray(group) ? group : [group];
      steps.forEach((step, colIndex) => {
        flat.push({ step, row: rowIndex, col: colIndex });
      });
    });

    flat.sort((a, b) => {
      const aRow = Number(a.step?.row);
      const bRow = Number(b.step?.row);
      const aCol = Number(a.step?.col);
      const bCol = Number(b.step?.col);
      const rowA = Number.isFinite(aRow) ? aRow : a.row;
      const rowB = Number.isFinite(bRow) ? bRow : b.row;
      const colA = Number.isFinite(aCol) ? aCol : a.col;
      const colB = Number.isFinite(bCol) ? bCol : b.col;
      if (rowA !== rowB) {
        return rowA - rowB;
      }
      return colA - colB;
    });

    return flat
      .map(({ step }, index) => {
        const data = step?.data && typeof step.data === 'object' ? step.data : {};
        const stageId = this.normalizeAssistantState(data.stage_id || data.state || data.id) || `STAGE_${index + 1}`;
        const buttonsRaw = Array.isArray(data.buttons) ? data.buttons : [];
        const buttons = buttonsRaw.map((button: any) => ({
          label: String(button?.label || ''),
          action: this.normalizeAssistantState(button?.action) || 'CUSTOM_ACTION',
          next_state: this.normalizeAssistantState(button?.next_state || button?.nextState) || '',
          output: String(button?.output || '')
        }));
        const stageType = this.normalizeAssistantStageType(
          data.stage_type || data.stage_action || data.action_type,
          stageId
        );
        return {
          id: stageId,
          title: String(data.title || stageId),
          text: String(data.text || data.assistant_text || ''),
          output: String(data.output || ''),
          stage_type: stageType,
          default_next_state: String(data.default_next_state || data.next_state || ''),
          timeout_seconds: Math.min(
            3600,
            Math.max(5, Number(data.timeout_seconds || data.processing_timeout_seconds || 90))
          ),
          timeout_next_state: String(data.timeout_next_state || data.next_timeout_state || ''),
          response_execute_api: String(data.response_execute_api || data.success_output || ''),
          failure_output: String(data.failure_output || ''),
          cancel_output: String(data.cancel_output || ''),
          allow_cancel_processing: Number(data.allow_cancel_processing || 0) === 1,
          buttons
        } as AssistantFlowStage;
      })
      .filter((stage) => !!stage.id);
  }

  private createEmptyAssistantButton(): AssistantFlowButton {
    return {
      label: '',
      action: 'CUSTOM_ACTION',
      next_state: '',
      output: ''
    };
  }

  private buildDefaultAssistantStage(index: number): AssistantFlowStage {
    const stageId = index === 1 ? 'AWAITING_START_CONFIRMATION' : `STAGE_${index}`;
    return {
      id: stageId,
      title: `Etapa ${index}`,
      text: '',
      output: '',
      stage_type: stageId === 'PROCESSING_SCOPE' ? ASSISTANT_STAGE_TYPE_EXECUTE_API : ASSISTANT_STAGE_TYPE_INTERACTION,
      default_next_state: this.noActionState,
      timeout_seconds: 90,
      timeout_next_state: this.noActionState,
      response_execute_api: '',
      failure_output: '',
      cancel_output: '',
      allow_cancel_processing: stageId === 'PROCESSING_SCOPE',
      buttons: []
    };
  }

  private ensureAssistantStageDefaultTransitions() {
    this.assistantStages = this.assistantStages.map((stage, index) => {
      const currentId = this.normalizeAssistantState(stage.id) || `STAGE_${index + 1}`;
      const nextId = this.normalizeAssistantState(this.assistantStages[index + 1]?.id || '');
      const configured = String(stage.default_next_state || '').trim().toUpperCase();
      let defaultNext = configured;
      if (!defaultNext) {
        defaultNext = nextId || this.noActionState;
      } else if (defaultNext !== this.noActionState) {
        defaultNext = this.normalizeAssistantState(defaultNext) || (nextId || this.noActionState);
      }
      const stageType = this.normalizeAssistantStageType(stage.stage_type, currentId);
      const timeoutNextRaw = String(stage.timeout_next_state || '').trim().toUpperCase();
      const timeoutNext =
        !timeoutNextRaw || timeoutNextRaw === this.noActionState
          ? this.noActionState
          : this.normalizeAssistantState(timeoutNextRaw) || this.noActionState;
      return {
        ...stage,
        id: currentId,
        stage_type: stageType,
        timeout_seconds:
          stageType !== ASSISTANT_STAGE_TYPE_INTERACTION
            ? Math.min(3600, Math.max(5, Number(stage.timeout_seconds || 90)))
            : 90,
        timeout_next_state: stageType !== ASSISTANT_STAGE_TYPE_INTERACTION ? timeoutNext : this.noActionState,
        response_execute_api: String(stage.response_execute_api || ''),
        failure_output: String(stage.failure_output || ''),
        cancel_output: String(stage.cancel_output || ''),
        allow_cancel_processing:
          stageType === ASSISTANT_STAGE_TYPE_EXECUTE_API ? Boolean(stage.allow_cancel_processing) : false,
        default_next_state: defaultNext
      };
    });
  }

  private buildDuplicatedAssistantStageId(baseId: string) {
    const normalizedBase = this.normalizeAssistantState(baseId) || 'STAGE';
    const allIds = new Set(
      this.assistantStages
        .map((stage) => this.normalizeAssistantState(stage.id))
        .filter(Boolean)
    );
    let candidate = `${normalizedBase}_COPY`;
    let suffix = 2;
    while (allIds.has(candidate)) {
      candidate = `${normalizedBase}_COPY_${suffix}`;
      suffix += 1;
    }
    return candidate;
  }

  private normalizeAssistantState(value: any): string | null {
    const normalized = String(value || '')
      .trim()
      .toUpperCase()
      .replace(/\s+/g, '_')
      .replace(/[^A-Z0-9_]/g, '');
    return normalized || null;
  }

  private normalizeAssistantStageType(value: any, stageId = ''): AssistantFlowStage['stage_type'] {
    const raw = String(value || '').trim().toUpperCase();
    if (raw === ASSISTANT_STAGE_TYPE_EXECUTE_API || raw === 'EXECUTE_AI' || raw === 'EXECUTAR_API') {
      return ASSISTANT_STAGE_TYPE_EXECUTE_API;
    }
    if (raw === ASSISTANT_STAGE_TYPE_TIMER) {
      return ASSISTANT_STAGE_TYPE_TIMER;
    }
    const normalizedStageId = this.normalizeAssistantState(stageId || '');
    if (normalizedStageId === 'PROCESSING_SCOPE') {
      return ASSISTANT_STAGE_TYPE_EXECUTE_API;
    }
    return ASSISTANT_STAGE_TYPE_INTERACTION;
  }

  private mapAssistantStageTypeToAction(type: AssistantFlowStage['stage_type'], stageId = ''): 'MESSAGE' | 'EXECUTE_AI' {
    const normalizedType = this.normalizeAssistantStageType(type, stageId);
    return normalizedType === ASSISTANT_STAGE_TYPE_EXECUTE_API ? 'EXECUTE_AI' : 'MESSAGE';
  }

  private computeNextId() {
    const maxId = this.rows.reduce((max, row) => {
      const rowMax = row.reduce((acc, step) => Math.max(acc, step.id || 0), 0);
      return Math.max(max, rowMax);
    }, 0);
    return maxId + 1;
  }

  typeLabel(type: string) {
    const item = this.palette.find((step) => step.type === type);
    return item?.title || 'Etapa';
  }

  moduleLabel(type: string) {
    switch (type) {
      case 'tarefa':
        return 'ControlTask';
      case 'chamado':
        return 'ControlHelp';
      case 'email':
        return 'E-mail';
      case 'condicao':
        return 'Condição';
      case 'inicio':
        return 'Início';
      case 'fim':
        return 'Conclusão';
      default:
        return 'Etapa';
    }
  }
}
